import { useState, useEffect, useCallback, memo, useMemo } from "react";
import Select, { StylesConfig } from "react-select";
import { Option } from "../../interface/Option";
import { MedicalCodesAxiosInstance } from "../../Network/MedicalCodesAxiosInstance";

const SingleIcdMedicalCodeSearch = memo(({ onChange, clear, defaultValue, description }: any) => {
  const [options, setOptions] = useState<Option[]>([]);
  const [selectedOption, setSelectedOption] = useState<Option | null>(defaultValue || null);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");
  const cache = useMemo(() => new Map<string, Option[]>(), []);

  const loadOptions = useCallback(async () => {
    if (inputValue.trim().length < 3 || loading) {
      setOptions([]);
      return;
    }

    const cacheKey = `${description}-${inputValue}`;
    if (cache.has(cacheKey)) {
      setOptions(cache.get(cacheKey)!);
      return;
    }

    setLoading(true);
    try {
      const response = await MedicalCodesAxiosInstance.get(`getIcd/${description}`, {
        params: { name: inputValue, limit: 10 },
      });
      const data = response.data?.result || [];

      const formattedOptions: Option[] = data
        .map((item: any) => ({
          value: item.icd.toString(),
          label: `${item.icd} - ${item.description}`,
        }))
        .filter((option: Option) => option.label.toLowerCase().includes(inputValue.toLowerCase()))
        .sort((a: Option, b: Option) => {
          const aStartsWith = a.label.toLowerCase().startsWith(inputValue.toLowerCase());
          const bStartsWith = b.label.toLowerCase().startsWith(inputValue.toLowerCase());
          if (aStartsWith && !bStartsWith) return -1;
          if (!aStartsWith && bStartsWith) return 1;
          return a.label.localeCompare(b.label);
        })
        .slice(0, 10);

      if (cache.size > 100) cache.clear();
      cache.set(cacheKey, formattedOptions);
      setOptions(formattedOptions);
    } catch (error) {
      console.error("Error loading options:", error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [inputValue, description, cache]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      loadOptions();
    }, 100);
    return () => clearTimeout(debounceTimeout);
  }, [inputValue, loadOptions]);

  const handleChange = useCallback(
    (selected: Option | null) => {
      setSelectedOption(selected);
      onChange(selected); 
    },
    [onChange]
  );

  useEffect(() => {
    if (clear) setSelectedOption(null);
  }, [clear]);

  useEffect(() => {
    if (defaultValue && JSON.stringify(defaultValue) !== JSON.stringify(selectedOption)) {
      setSelectedOption(defaultValue);
    }
  }, [defaultValue]);

  const customStyles: StylesConfig<Option, false> = useMemo(
    () => ({
      option: (styles, { isSelected }) => ({
        ...styles,
        backgroundColor: isSelected ? "#007BFF" : "#FFFFFF",
        color: isSelected ? "#FFFFFF" : "#000000",
        ":hover": { backgroundColor: "#0056b3", color: "#FFFFFF" },
      }),
      control: (styles) => ({
        ...styles,
        minHeight: "38px",
        borderColor: "#ccc",
        boxShadow: "none",
        ":hover": { borderColor: "#888" },
      }),
    }),
    []
  );

  return (
    <Select
      isMulti={false}
      value={selectedOption}
      onChange={handleChange}
      options={options}
      isLoading={loading}
      onInputChange={setInputValue}
      placeholder="Type at least 3 letters to search..."
      styles={customStyles}
      noOptionsMessage={() =>
        loading ? "Loading..." : inputValue.length < 3 ? "Type at least 3 letters..." : "No options found"
      }
      maxMenuHeight={150}
    />
  );
});

SingleIcdMedicalCodeSearch.displayName = "SingleIcdMedicalCodeSearch";
export default SingleIcdMedicalCodeSearch;
